<template>
<div class="container-fluid">
    <div class="row">

        <div class="col-12">
            <div class="d-flex align-items-center">
                <h1 class="flex-grow-1" data-test="pageTitle">Manage Specialties</h1>
                
                <div v-if="perms.can_administer_specialties" class="drop-down drop-down-show text-transform-none">
                    <span 
                        role="button" id="dropdownMenuLink" data-toggle="dropdown" aria-haspopup="true" aria-expanded="false"
                        class="btn btn-sm btn-ghost-primary text-gray-500 cursor-pointer leading-none"
                        v-tooltip.bottom="'Options'"
                        data-test="specsContextMenu">
                        <i class="far fa-cog text-xl"></i>
                    </span>

                    <div class="dropdown-menu py-0 mr-4" aria-labelledby="dropdownMenuLink">
                        <router-link exact :to="{ name: 'admin.specialties' }" class="dropdown-item">Manage Specialties</router-link>
                        <router-link exact :to="{ name: 'admin.specialties.mappings' }" class="dropdown-item">Manage Mappings</router-link>
                    </div>
                </div>
            </div>
        </div>

        <div class="col-8" :class="{ 'pending' : pending }">
            <div v-if="specialtiesChanged" class="alert alert-warning bg-yellow-200 d-flex align-items-center justify-content-between">
                <div>Specialties have been changed. <router-link exact :to="{ name: 'admin.specialties.mappings' }"><span @click="hideChanged()">Review mappings</span></router-link> for imported physicians.</div>
                <router-link exact :to="{ name: 'admin.specialties.mappings' }" class="btn btn-sm btn-primary text-nowrap"><span @click="hideChanged()">Mappings</span></router-link>
            </div>

            <div v-if="addForm" class="card">
                <div class="card-header">Add Specialties</div>
                <div class="card-body">
                    Select items to add, or type in new specialties
                    <v-select
                        multiple
                        taggable
                        :options="availableSpecialtiesToAdd.filter(item => addSpecialties.indexOf(item) < 0)"
                        v-model="addSpecialties"
                    >
                        <template v-slot:option="option">
                            {{ option.label }}
                            <span v-if="option.default" class="badge badge-info">Default</span>
                        </template>
                    </v-select>

                    <div class="mt-2 text-right">
                        <span class="btn btn-ghost-secondary" @click="displayAddForm(false)">Cancel</span>
                        <span class="btn btn-primary" @click="saveNewSpecialties()">Add Specialties</span>
                    </div>
                </div>
            </div> 

        	<div class="card mb-4">
                <table class="table table-hover mb-0">
                	<thead>
                        <th class="spec-check">Enabled</th>
                		<th>Label (count)</th>
                        <th class="action text-right">
                            <span
                                v-if="perms.can_administer_specialties"
                                @click="displayAddForm(true)"
                                v-tooltip.bottom="'Add Specialty'"
                                class="btn btn-sm btn-ghost-primary text-gray-500 cursor-pointer leading-none"
                                data-test="addLSpecialtiesButton" >
                                <i class="far fa-plus-circle text-base" />
                            </span>                            
                        </th>
                	</thead>
                	<tr v-for="specialty in allSpecialties.filter(item => clientSpecialtiesOriginal.includes(item.id))">
                        <td class="spec-check align-middle">
                            <input type="checkbox" :value="specialty.id" v-model="clientSpecialtiesChanged">
                        </td>
                		<td class="align-middle w-80">
                			<template v-if="specialty.id === editing.id">				
                				<input class="form-control form-control-sm" v-model="editing.label">
                			</template>
                			<template v-else>
                				{{ specialty.label }} ({{ specialty.physicians_count + specialty.groups_count }}) <span v-if="specialty.default" class="badge badge-info" v-tooltip.bottom="'Set by Ember. Can be disabled but not edited.'">Default</span>
                			</template>
                		</td>
                		<td class="text-right align-middle w-40">
                			<template v-if="specialty.id === editing.id">
                				<button class="btn btn-sm btn-primary" @click="!specialty.client_id && !specialty.default ? saveCustomizedSpecialty(specialty.id) : updateSpecialty(specialty.id)">Save</button>
                				<button class="btn btn-sm btn-ghost-secondary" @click="resetEditing(specialty.id)">Cancel</button>
                				<button class="btn btn-sm btn-ghost-danger" @click="specialty.client_id ? deleteSpecialty(specialty.id) : detachSpecialty(specialty.id)">Delete</button>
                			</template>

                			<template v-else>
                				<button
                                    v-if="!specialty.default && specialty.can.manage == true"
                                    class="btn btn-sm btn-ghost btn-ghost-primary"
                                    @click="setEditing(specialty.id)">
                                    Edit
                                </button>
                			</template>		
                		</td>
                	</tr>
                    <tr>
                        <td colspan=3 class="text-right">
                            <span class="btn btn-primary" @click="saveClientSpecialties()">Save Specialties</span>
                        </td>
                    </tr>
                </table>
            </div>
        </div>

        <div class="col-4">
            <div class="card">
                <div class="card-header">
                    EMR Specialties (physician count)
                </div>
                <div class="card-body">
                    <div v-for="specialty in emrSpecialties" class="py-1">
                        {{ specialty.label ? specialty.label : 'Null/none' }} <span class="text-gray-700">({{ specialty.SpecialtyCount }})</span>
                    </div>
                </div>
            </div>
        </div>
    </div>
</div>
</template>

<script>
    import { mapState } from 'vuex';
    import BreadcrumbsManager from '../../helpers/BreadcrumbManager';

	export default {
        mixins: [BreadcrumbsManager],

        data() {
            return {
                addForm: false,
                addSpecialties: [],

                clientSpecialtiesOriginal: [],
                clientSpecialtiesChanged: [],
                allSpecialties: [],
                specs: [],

                emrSpecialties: [],

                editing: {
                	id: '',
                	label: '',
                },

                oldLabel: '',
                pending: false,

                specialtiesChanged: false,
            }
        },

        created() {
        	this.fetchAllSpecialties();
            this.fetchEmrSpecialties();
        },

        mounted() {
            this.setEnabledSpecialties();   
        },

        activated() {
            this.setBreadcrumbs([
                { text: 'Home', to: { name: 'home' } },
                { text: 'Settings', to: { name: '' } },
                { text: 'Specialties', to: { name: 'admin.specialties' } },
            ]);   
        },

        methods: {


            displayAddForm(value) {
                this.addForm = value;
            },


        	fetchAllSpecialties() {
                axios.get(`/api/specialties/all`).then(({data}) => {
                    this.allSpecialties = data;
                });
        	},

            fetchEmrSpecialties() {
                axios.get(`/api/specialties/counts`).then(({data}) => {
                    this.emrSpecialties = data;
                });
            },

            hideChanged() {
                this.specialtiesChanged = false;
            },

            resetEditing(id) {
                this.editing.id = '';
                this.allSpecialties.find(x => x.id === id).label = this.oldLabel;
            },

            setEnabledSpecialties() {
                this.clientSpecialtiesOriginal = this.options.specialties.map(spec => spec.id);
                this.clientSpecialtiesChanged = this.options.specialties.map(spec => spec.id);
            },

        	setEditing(id) {
                if (id) {
                    this.editing.id = id;
                    this.editing.label = this.allSpecialties.find(x => x.id === id).label;
                    this.oldLabel = this.allSpecialties.find(x => x.id === id).label;
                } else {
                    this.editing.id = '';
                    this.editing.label = '';
                    this.oldLabel = '';
                }
        	},


            saveClientSpecialties() {
                Swal.fire({
                    title: 'Are you sure?',
                    text: 'Disabled specialties will not be available for display or selection',
                    icon: 'warning',
                    showCancelButton: true,
                    confirmButtonText: 'Save specialties',
                    cancelButtonText: 'Cancel',
                    animation: false,
                }).then((result) => {
                    if (result.value) {
                        axios.post('/api/specialties/client-sync', this.clientSpecialtiesChanged).then(({data}) => {
                            this.$store.dispatch('refreshSpecialties');
                            this.specialtiesChanged = true;
                            Swal.fire({
                                toast: true,
                                position: 'top-end',
                                showConfirmButton: false,
                                timer: 4000,
                                icon: 'success',
                                title: 'Specialties saved',
                            });                             
                        });
                    }
                });
            },

            saveCustomizedSpecialty(id) {
                this.pending = true;
                axios.post(`/api/specialties/${id}/customize`, this.editing).then(({data}) => {
                    this.allSpecialties.find(x => x.id === id).label = data.label;
                    this.editing.id = null;
                    this.editing.label = null;
                    this.$store.dispatch('refreshSpecialties');
                    this.specialtiesChanged = true;
                    this.pending = false;
                })
            },

            async saveNewSpecialties() {
                this.pending = true;
                //Free-typed entries need to be added
                if (this.addSpecialties.filter(item => !item.id).length > 0) {
                    await axios.post('/api/specialties', this.addSpecialties.filter(item => !item.id));
                }

                //Existing ones need to be synced with the client
                if (this.addSpecialties.filter(item => item.id).length > 0) {
                    await axios.post('/api/specialties/client-add', this.addSpecialties.filter(item => item.id).map(item => item.id));
                }

                this.fetchAllSpecialties();
                this.$store.dispatch('refreshSpecialties');
                this.pending = false;
                this.specialtiesChanged = true;
                this.addSpecialties = [];
                this.displayAddForm(false);
            },

            updateSpecialty(id) {
                this.pending = true;
                axios.put(`/api/specialties/${id}`, this.editing).then(({data}) => {
                    this.fetchAllSpecialties();
                    this.setEditing(null);
                    this.specialtiesChanged = true;
                    this.$store.dispatch('refreshSpecialties');
                    this.pending = false;
                })
            },

            deleteSpecialty(id) {
                Swal.fire({
                    title: 'Are you sure?',
                    text: 'You will not be able to recover this specialty!',
                    icon: 'warning',
                    showCancelButton: true,
                    confirmButtonText: 'Delete specialty',
                    cancelButtonText: 'Keep specialty',
                    animation: false,
                }).then((result) => {
                    if (result.value) {
                        axios.delete(`/api/specialties/${id}`, id).then(() => {
                        	let index = this.allSpecialties.map(function (lines) { return lines.id; }).indexOf(id);
                        	//this.allSpecialties.splice(index, 1);
                            this.fetchAllSpecialties();
                            this.$store.dispatch('refreshSpecialties');
                            this.specialtiesChanged = true;
                        })
                    }
                });
            },

            detachSpecialty(id) {
                Swal.fire({
                    title: 'Are you sure?',
                    text: 'You will not be able to recover this specialty!',
                    icon: 'warning',
                    showCancelButton: true,
                    confirmButtonText: 'Delete specialty',
                    cancelButtonText: 'Keep specialty',
                    animation: false,
                }).then((result) => {
                    if (result.value) {
                        axios.post(`/api/specialties/client-remove`, [id]).then(() => {
                            let index = this.allSpecialties.map(function (lines) { return lines.id; }).indexOf(id);
                            //this.allSpecialties.splice(index, 1);
                            this.fetchAllSpecialties();
                            this.$store.dispatch('refreshSpecialties');
                            this.specialtiesChanged = true;
                        })
                    }
                });
            },            
        },

        computed: {
            ...mapState(['options', 'perms']),

            availableSpecialtiesToAdd() {
                return this.allSpecialties.filter(item => !this.clientSpecialtiesChanged.includes(item.id));
            }
        },

        watch: {
            '$store.state.options.specialties'() {
                this.setEnabledSpecialties();
            }
        }
    }
</script>

<style scoped>
    .spec-check { width:60px; }
    .actions { width:120px; }
</style>